"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseFeedbackTriggerOptions {
  /** Delay in ms before modal opens after trigger. Default: 0 */
  delay?: number;
  /** URL search param key for the application ID. Default: "id" */
  paramKey?: string;
}

interface UseFeedbackTriggerReturn {
  /** The applicationId from URL param */
  applicationId: string;
  /** Whether the feedback modal should be open */
  feedbackOpened: boolean;
  /** Call on user action. Returns true if feedback was triggered (caller should defer its action). */
  handleTrigger: () => boolean;
  /** Call when modal closes. */
  handleClose: () => void;
}

export function useFeedbackTrigger({
  delay = 0,
  paramKey = "id",
}: UseFeedbackTriggerOptions = {}): UseFeedbackTriggerReturn {
  const searchParams = useSearchParams();
  const applicationId = searchParams.get(paramKey) ?? "";

  const [feedbackOpened, setFeedbackOpened] = useState(false);
  const feedbackTriggered = useRef(false);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const delayRef = useRef(delay);
  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  const handleTrigger = useCallback((): boolean => {
    if (!applicationId || feedbackTriggered.current) return false;
    feedbackTriggered.current = true;
    feedbackTimerRef.current = setTimeout(
      () => setFeedbackOpened(true),
      delayRef.current,
    );
    return true;
  }, [applicationId]);

  const handleClose = useCallback(() => {
    setFeedbackOpened(false);
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  return {
    applicationId,
    feedbackOpened,
    handleTrigger,
    handleClose,
  };
}
